/**
 * Pause de saisie Vevidental — contrat partagé.
 *
 * Pourquoi cette notion existe (incident fondateur du 2026-07-30) : la secrétaire
 * était absente et sa remplaçante n'utilisait pas Vevidental. Le laboratoire
 * produisait normalement, mais plus rien n'entrait dans la chaîne. La règle
 * `reception_silencieuse` criait donc au loup en tête du tableau de bord, tous
 * les matins, avec un compteur qui montait — le meilleur moyen d'apprendre à
 * l'équipe à ignorer l'alerte la plus importante de la page.
 *
 * Deux exigences en découlent, et elles se tiennent :
 *  1. NEUTRALISER l'alerte quand l'absence de saisie est CONNUE et déclarée ;
 *  2. ne jamais faire croire pour autant que les chiffres sont complets — le
 *     bandeau dit ce qui manque, et le rattrapage reste signalé après la reprise.
 *
 * PIÈGE À NE PAS REFAIRE : ceci n'est PAS une fermeture exceptionnelle. Les
 * fermetures (`fermeture_exceptionnelle`) retirent le jour du calendrier ouvré et
 * décaleraient toutes les échéances, masquant de vrais retards. Ici le
 * laboratoire TRAVAILLE : seule la saisie est suspendue.
 */
import { z } from "zod";

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date attendue au format AAAA-MM-JJ");

export const LIMITE_MOTIF_PAUSE = 200;

export const PauseSaisieSchema = z.object({
  id: z.number().int().positive(),
  debut: IsoDate,
  /** null = pause en cours, sans date de fin connue */
  fin: IsoDate.nullable(),
  motif: z.string().min(1),
  auteur: z.string().min(1),
});
export type PauseSaisie = z.infer<typeof PauseSaisieSchema>;

export const DeclarerPauseSchema = z
  .object({
    debut: IsoDate,
    fin: IsoDate.nullable().default(null),
    motif: z.string().trim().min(3, "motif trop court").max(LIMITE_MOTIF_PAUSE),
  })
  .refine((v) => v.fin === null || v.fin >= v.debut, {
    message: "la fin ne peut pas précéder le début",
    path: ["fin"],
  });
export type DeclarerPause = z.infer<typeof DeclarerPauseSchema>;

/**
 * Combien de temps après la reprise les cadences restent suspectes.
 *
 * Au rattrapage, les travaux entrent avec la date du jour de SAISIE et non leur
 * vraie date de réception : un cabinet muet depuis trois semaines paraît soudain
 * actif, et inversement. Une semaine couvre le rattrapage courant sans installer
 * un avertissement permanent — un avertissement permanent ne se lit plus.
 */
export const JOURS_PRUDENCE_APRES_REPRISE = 7;

function joursEntre(depuis: string, jusqua: string): number {
  return Math.round((Date.parse(`${jusqua}T00:00:00Z`) - Date.parse(`${depuis}T00:00:00Z`)) / 86_400_000);
}

/** La pause qui couvre `jour`, s'il y en a une (la plus récemment commencée). */
export function pauseActive(pauses: readonly PauseSaisie[], jour: string): PauseSaisie | null {
  const candidates = pauses.filter((p) => p.debut <= jour && (p.fin === null || p.fin >= jour));
  return candidates.sort((a, b) => b.debut.localeCompare(a.debut))[0] ?? null;
}

/**
 * La pause terminée depuis peu — le rattrapage de saisie fausse encore les
 * cadences. Renvoie null s'il y a une pause active (on n'a pas encore repris).
 */
export function pauseEnRattrapage(pauses: readonly PauseSaisie[], jour: string): PauseSaisie | null {
  if (pauseActive(pauses, jour)) return null;
  const terminees = pauses.filter(
    (p) => p.fin !== null && p.fin < jour && joursEntre(p.fin, jour) <= JOURS_PRUDENCE_APRES_REPRISE,
  );
  return terminees.sort((a, b) => (b.fin ?? "").localeCompare(a.fin ?? ""))[0] ?? null;
}

/**
 * Pastille « détectée pendant la pause de saisie » (sprint 2) — prédicat PUR,
 * calculé côté serveur à la lecture, jamais écrit en base.
 *
 * Une action RELATION née pendant une pause (ou dont l'échéance théorique
 * tombe dedans) accuse un silence qui vient de la SAISIE arrêtée, pas du
 * cabinet : l'écran doit le dire. Vit ici et pas dans relances.ts — relances
 * n'importe pas pauses, l'inverse créerait un cycle.
 */
export function detecteePendantPause(
  pauses: readonly PauseSaisie[],
  action: { queue: string; createdAt: string; theoreticalDueAt: string | null },
): boolean {
  if (action.queue !== "RELATION") return false;
  if (pauseActive(pauses, action.createdAt.slice(0, 10))) return true;
  return action.theoreticalDueAt !== null && pauseActive(pauses, action.theoreticalDueAt) !== null;
}

/** État de la saisie servi au tableau de bord — porte le bandeau d'honnêteté. */
export const EtatSaisieSchema = z.object({
  enPause: z.boolean(),
  depuis: IsoDate.nullable(),
  /** null quand la pause n'a pas de fin annoncée */
  jusqua: IsoDate.nullable(),
  motif: z.string().nullable(),
  /** true dans la semaine qui suit la reprise : les cadences sont encore faussées */
  rattrapage: z.boolean(),
});
export type EtatSaisie = z.infer<typeof EtatSaisieSchema>;

export const ETAT_SAISIE_NORMAL: EtatSaisie = {
  enPause: false,
  depuis: null,
  jusqua: null,
  motif: null,
  rattrapage: false,
};

export function etatSaisie(pauses: readonly PauseSaisie[], jour: string): EtatSaisie {
  const active = pauseActive(pauses, jour);
  if (active) {
    return { enPause: true, depuis: active.debut, jusqua: active.fin, motif: active.motif, rattrapage: false };
  }
  const reprise = pauseEnRattrapage(pauses, jour);
  if (reprise) {
    return { enPause: false, depuis: reprise.debut, jusqua: reprise.fin, motif: reprise.motif, rattrapage: true };
  }
  return ETAT_SAISIE_NORMAL;
}

/** « 28/07 » — les bandeaux parlent en jours, pas en ISO. */
export function jourCourt(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d ?? "??"}/${m ?? "??"}`;
}
